import {
  type ComponentProps,
  type ElementRef,
  type FC,
  forwardRef,
  type ReactNode,
} from 'react';

import { useComposition } from '@/hooks/use-composition';
import { Button } from '@/components/ui/button';
import ChatAvatar from '@/components/ui/chat-avatar';
import ChatStatus from '@/components/ui/chat-status';

interface ChatHeaderButtonProps extends ComponentProps<typeof Button> {
  children: ReactNode;
  onClick: () => void;
}
const ChatHeaderButton = forwardRef<
  ElementRef<typeof Button>,
  ChatHeaderButtonProps
>(({ children, onClick, ...props }, ref) => (
  <Button
    ref={ref}
    {...props}
    onClick={onClick}>
    <Button.Icon icon>{children}</Button.Icon>
  </Button>
));
ChatHeaderButton.displayName = 'ChatHeaderButton';

interface ChatHeaderProps {
  children: ReactNode;
}
const ChatHeader: FC<ChatHeaderProps> = ({ children: _children }) => {
  const [children, avatar, indicator, buttons] = useComposition(
    _children,
    ChatAvatar.displayName,
    ChatStatus.displayName,
    ChatHeaderButton.displayName
  );
  return (
    <div className='mx-3 flex flex-row justify-between'>
      <div className='grid grid-cols-[auto_auto] grid-rows-[auto_auto] gap-x-3 gap-y-1'>
        <div className='col-start-1 row-span-2 row-start-1 flex items-center'>
          {avatar}
        </div>
        <div className='col-start-2 row-start-1 text-base'>{children}</div>
        <div className='col-start-2 row-start-2'>{indicator}</div>
      </div>
      {(buttons ?? []).length > 0 && (
        <div className='flex flex-row items-center gap-2'>{buttons}</div>
      )}
    </div>
  );
};
ChatHeader.displayName = 'ChatHeader';

interface ChatHeaderComposition {
  Avatar: typeof ChatAvatar;
  Status: typeof ChatStatus;
  Button: typeof ChatHeaderButton;
}

const RootWithComposition: typeof ChatHeader & ChatHeaderComposition =
  Object.assign(ChatHeader, {
    Avatar: ChatAvatar,
    Status: ChatStatus,
    Button: ChatHeaderButton,
  });

export { RootWithComposition as ChatHeader };
